import { useState, useRef, useEffect, type ReactNode } from "react";
import { useLocation } from "@tanstack/react-router";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { MobileDrawer } from "./MobileDrawer";
import { PageGuideLocationProvider } from "@/features/page-guide/location-context";
import { ProductLabelProvider } from "@/features/products/ProductLabelProvider";
import { PersonaBand } from "@/features/persona/PersonaBand";
import { usePersonaSync } from "@/features/persona/usePersonaSync";
import { IncidentReadGate } from "@/features/incident-read/IncidentReadGate";

/**
 * Routes that fill the whole content area instead of the 1440px reading column.
 * A calendar grid is not prose: capping it at 1440px on a wider screen wastes
 * the exact width the day columns need, and the leftover margin reads as an
 * unexplained gap. Keep this list short — the default is the capped column.
 */
const FULL_WIDTH_ROUTES = ["/schedule"];

/**
 * Hai màn làm việc với ẢNH cũng dùng hết bề ngang (QĐ 0218 lượt 6): bản xem
 * thử CEO duyệt ghi rõ "hai cột hai bên giữ nguyên, phần giữa nở ra hết chỗ
 * còn lại — màn càng rộng càng nhiều ảnh trên một hàng". Chặn ở 1440px thì
 * trên màn 1920 của thợ sửa, lưới mất đúng một làn ảnh.
 *
 * Trang Sửa hình chỉ mở rộng ở thẻ "Đơn chờ sửa": hai thẻ kia là bảng chữ,
 * vẫn đọc ở cột 1440 như mọi trang.
 */
function isPhotoWorkspace(pathname: string, tab: string | null): boolean {
  if (pathname.startsWith("/gallery/orders/")) return true;
  // Trang Quản lý album (QĐ 0301) là lưới ảnh: bó ở 1440px thì màn 1920 không
  // bao giờ lên được 6 cột như bản xem thử CEO duyệt. So ĐÚNG "/albums" — các
  // trang con sau này (sổ giám đốc là bảng chữ) vẫn đọc ở cột 1440.
  if (pathname === "/albums") return true;
  return pathname === "/retouch" && tab === "photoqueue";
}

/**
 * Màn Chat khách: nội dung sát bốn mép và TỰ CUỘN từng cột (danh sách / khung
 * tin), main không cuộn chung. Cột menu + thanh đầu giữ như mọi màn (CEO chốt
 * 1a 15/10/2026 — bỏ dải biểu tượng 64px của QĐ 0223).
 */
export function isSelfScrollingRoute(pathname: string): boolean {
  return pathname === "/customer-chats";
}

// Layout shell mirrors FE bk: 260px sidebar + 56px topbar + content area
// with 24px padding capped at 1440px max-width. Background defaults to
// bg (warm cream) via tokens so the white surface for cards/header
// stands out against it.
export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  usePersonaSync();
  const { pathname, search } = useLocation();
  const rawTab = (search as Record<string, unknown> | undefined)?.tab;
  const tab = typeof rawTab === "string" ? rawTab : null;
  const selfScroll = isSelfScrollingRoute(pathname);
  const fullWidth =
    FULL_WIDTH_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`)) ||
    isPhotoWorkspace(pathname, tab);

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [pathname]);

  return (
    <PageGuideLocationProvider pathname={pathname} tab={tab}>
      <div className="flex h-screen flex-col overflow-hidden bg-bg">
        {/* QĐ 0255: dải "Đang dùng với tư cách …" nằm trên cùng, trên cả cột menu. */}
        <PersonaBand />
        {/* Sổ vi phạm: phiếu lỗi đã xác nhận chưa đọc thì chặn cả app (CEO 3a + 5a). */}
        <IncidentReadGate />
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <Sidebar />
          <MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
          <div className="flex min-w-0 flex-1 flex-col">
            <Header onMenuToggle={() => setDrawerOpen(true)} />
            {/* Màn tự cuộn từng cột: main không cuộn, nếu không thì cả cột danh
                sách lẫn khung tin cùng trôi theo một thanh cuộn. */}
            <main
              ref={mainRef}
              className={selfScroll ? "min-h-0 flex-1 overflow-hidden" : "min-h-0 flex-1 overflow-auto"}
            >
              <div
                data-full-width={fullWidth || selfScroll || undefined}
                data-self-scroll={selfScroll || undefined}
                className={selfScroll || fullWidth ? "h-full w-full" : "mx-auto w-full max-w-[1440px]"}
                style={
                  selfScroll
                    ? undefined
                    : fullWidth
                      ? { padding: "var(--space-page-y-tight) var(--space-page-x-tight)" }
                      : { padding: "var(--space-page-y) var(--space-page-x)" }
                }
              >
                {/* Tên gói theo danh mục máy chủ cho mọi màn đã đăng nhập (B6). */}
                <ProductLabelProvider>{children}</ProductLabelProvider>
              </div>
            </main>
          </div>
        </div>
      </div>
    </PageGuideLocationProvider>
  );
}
